import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";

const MOBILE_NAV_MQ = `@media ${MOBILE_NAV_MEDIA}`;

/** Shared V2 dialog paper corner. */
export const DIALOG_PAPER_RADIUS = "6px";

/** AAA primary action — deep gold (Accept / Join). */
export const DIALOG_ACCEPT_BUTTON_SX = {
    position: "relative",
    overflow: "hidden",
    color: colors.text.contrast,
    fontWeight: 750,
    borderRadius: "12px",
    border: "1px solid rgba(255,210,120,0.22)",
    background: `
        linear-gradient(
            180deg,
            #E0A84A 0%,
            #C9851F 48%,
            #A66A14 100%
        )
    `,
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.28),
        inset 0 -2px 0 rgba(90,45,0,0.22),
        0 10px 24px rgba(160,100,20,0.28)
    `,
    transition:
        "filter 0.15s ease-in-out, box-shadow 0.15s ease-in-out, border-color 0.15s ease-in-out",
    "&::after": {
        content: '""',
        position: "absolute",
        left: "12%",
        right: "12%",
        top: 0,
        height: "1px",
        background: `
            linear-gradient(
                90deg,
                transparent,
                rgba(255,255,255,0.55),
                transparent
            )
        `,
        pointerEvents: "none",
    },
    "&:hover": {
        filter: "brightness(1.04)",
        borderColor: "rgba(255,210,120,0.32)",
        background: `
            linear-gradient(
                180deg,
                #E8B255 0%,
                #D8922E 48%,
                #B8741F 100%
            )
        `,
        boxShadow: `
            inset 0 1px 0 rgba(255,255,255,0.34),
            inset 0 -2px 0 rgba(90,45,0,0.24),
            0 12px 28px rgba(160,100,20,0.34)
        `,
    },
    "&:disabled": {
        color: alpha("#fff", 0.38),
        border: "1px solid rgba(255,255,255,0.06)",
        background: `
            linear-gradient(
                180deg,
                rgba(255,255,255,0.045) 0%,
                rgba(255,255,255,0.02) 100%
            )
        `,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
        filter: "none",
        cursor: "not-allowed",
        "&::after": {
            opacity: 0,
        },
        "& .MuiButton-endIcon": {
            color: "inherit",
            opacity: 0.55,
        },
    },
};

/** AAA destructive action — dark crimson (Decline / Exit / Confirm). */
export const DIALOG_DECLINE_BUTTON_SX = {
    position: "relative",
    overflow: "hidden",
    color: alpha("#fff", 0.88),
    fontWeight: 750,
    borderRadius: "12px",
    border: "1px solid rgba(255,120,120,0.16)",
    background: `
        linear-gradient(
            180deg,
            rgba(92, 28, 28, 0.95) 0%,
            rgba(58, 16, 16, 0.98) 52%,
            rgba(40, 12, 12, 1) 100%
        )
    `,
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.08),
        inset 0 -2px 0 rgba(0,0,0,0.28),
        0 10px 24px rgba(0,0,0,0.32)
    `,
    transition:
        "filter 0.15s ease-in-out, box-shadow 0.15s ease-in-out, border-color 0.15s ease-in-out, color 0.15s ease-in-out",
    "&::after": {
        content: '""',
        position: "absolute",
        left: "12%",
        right: "12%",
        top: 0,
        height: "1px",
        background: `
            linear-gradient(
                90deg,
                transparent,
                rgba(255,255,255,0.18),
                transparent
            )
        `,
        pointerEvents: "none",
    },
    "&:hover": {
        filter: "brightness(1.08)",
        color: "#fff",
        borderColor: "rgba(255,120,120,0.28)",
        background: `
            linear-gradient(
                180deg,
                rgba(110, 34, 34, 1) 0%,
                rgba(72, 18, 18, 1) 52%,
                rgba(48, 14, 14, 1) 100%
            )
        `,
        boxShadow: `
            inset 0 1px 0 rgba(255,255,255,0.1),
            inset 0 -2px 0 rgba(0,0,0,0.3),
            0 12px 28px rgba(80, 18, 18, 0.35)
        `,
    },
    "&:disabled": {
        color: "rgba(255,255,255,0.32)",
        border: `1px solid ${colors.border.default}`,
        background: `
            linear-gradient(
                180deg,
                rgba(55,55,55,0.9) 0%,
                rgba(38,38,38,0.9) 100%
            )
        `,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
        filter: "none",
        cursor: "not-allowed",
    },
};

/** Leave Lobby / destructive confirm — same control as the lobby exit button. */
export const LOBBY_EXIT_BUTTON_SX = {
    background: `
        linear-gradient(
            180deg,
            rgba(140, 32, 32, 0.96) 0%,
            rgba(92, 18, 18, 0.96) 100%
        )
    `,
    color: colors.text.primary,
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: { xs: "0.625rem", xl: "0.70rem" },
    fontWeight: 600,
    padding: { xs: "0.75rem 1.25rem", xl: "0.8rem 1.45rem" },
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    position: "relative",
    overflow: "hidden",
    minWidth: 0,
    lineHeight: 1.2,
    transition: `
        transform 140ms cubic-bezier(0.2, 0.8, 0.2, 1),
        box-shadow 180ms ease,
        background 220ms ease,
        border-color 220ms ease
    `,
    boxShadow: `
        0 1px 0 rgba(255,255,255,0.06) inset,
        0 -1px 0 rgba(0,0,0,0.28) inset,
        0 8px 20px rgba(0,0,0,0.34)
    `,
    [MOBILE_NAV_MQ]: {
        fontSize: "0.62rem",
        fontWeight: 600,
        padding: "0.675rem 0.85rem",
        borderRadius: "8px",
        letterSpacing: "0.06em",
        lineHeight: 1.15,
        "&:hover": {
            transform: "none",
        },
        "&:active": {
            transform: "none",
        },
    },
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: `
            radial-gradient(
                circle at top,
                rgba(255,255,255,0.10),
                transparent 72%
            )
        `,
        opacity: 0.8,
        pointerEvents: "none",
    },
    "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        borderRadius: "inherit",
        background: `
            linear-gradient(
                135deg,
                transparent 0%,
                rgba(255,255,255,0.03) 50%,
                transparent 100%
            )
        `,
        opacity: 0,
        transition: "opacity 180ms ease",
    },
    "&:hover": {
        background: `
            linear-gradient(
                180deg,
                rgba(152, 38, 38, 0.98) 0%,
                rgba(104, 22, 22, 0.98) 100%
            )
        `,
        transform: "translateY(-1px) scale(1.01)",
        boxShadow: `
            0 1px 0 rgba(255,255,255,0.08) inset,
            0 -1px 0 rgba(0,0,0,0.32) inset,
            0 12px 28px rgba(0,0,0,0.42)
        `,
        "&::after": {
            opacity: 1,
        },
    },
    "&:active": {
        transform: "translateY(1px) scale(0.992)",
        boxShadow: `
            0 1px 0 rgba(255,255,255,0.04) inset,
            0 4px 12px rgba(0,0,0,0.3)
        `,
    },
    "&:disabled": {
        background: `
            linear-gradient(
                180deg,
                rgba(55,55,55,0.9) 0%,
                rgba(38,38,38,0.9) 100%
            )
        `,
        color: "rgba(255,255,255,0.32)",
        boxShadow: `
            0 1px 0 rgba(255,255,255,0.03) inset
        `,
        cursor: "not-allowed",
        transform: "none",
    },
};

/** Flat cancel button for dialogs — matches deletion dialog style. */
export const DIALOG_CANCEL_BUTTON_SX = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    appearance: "none",
    WebkitAppearance: "none",
    m: 0,
    flex: "0 0 auto",
    width: "auto",
    minWidth: "8.5rem",
    height: "2.5rem",
    padding: "0 1.25rem",
    border: "none",
    outline: "none",
    borderRadius: "8px",
    background: "rgba(255,255,255,0.04)",
    color: colors.text.secondary,
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    lineHeight: 1,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    cursor: "pointer",
    transition: "background 0.15s ease-in-out, color 0.15s ease-in-out",
    "&:hover:not(:disabled)": {
        color: colors.text.primary,
        background: "rgba(255,255,255,0.07)",
    },
    "&:disabled": {
        opacity: 0.55,
        cursor: "not-allowed",
    },
    "&:focus, &:focus-visible": {
        outline: "none",
    },
};
